'use client';

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ImageProps as NextImageProps } from 'next/image';
import NextImage from 'next/image';
import { cx } from '../utils/cx';
import {
  buildImageDerivativeUrl,
  getFilesOrigin,
  resolveAssetUrl,
  sanitizeUrl,
} from '../utils/asset-url';

export interface ImageProps
  extends Omit<
    NextImageProps,
    'src' | 'alt' | 'width' | 'height' | 'fill' | 'className' | 'style'
  > {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill' | 'scale-down';
  objectPosition?: string;
  className?: string;
  imgClassName?: string;
  fallbackSrc?: string;
  imgStyle?: CSSProperties;
}

/**
 * Matches pre-built localized derivative URLs served by the files host, e.g.
 * https://mneno-files.theai.app/images/en/640/some/article-path.webp
 *
 * These URLs are already final: they carry the locale and the requested
 * width in the path and point at an optimized .webp derivative, so neither
 * the client-side derivative builder nor Next's image optimizer may touch
 * them again.
 */
const LOCALIZED_DERIVATIVE_URL_RE =
  /^https?:\/\/[^/]+\/images\/[^/]+\/\d+\//;

export function Image({
  src,
  alt,
  width,
  height,
  fill = false,
  objectFit = 'cover',
  objectPosition = 'center',
  className,
  imgClassName,
  fallbackSrc,
  imgStyle,
  ...rest
}: ImageProps) {
  const filesOrigin = useMemo(() => getFilesOrigin(), []);

  // Request a webp derivative at twice the declared display width for
  // high-DPR screens. Fill images without a width retain their original URL.
  // SVGs (like the placeholder fallback) also keep their original URLs.
  // Pre-built localized derivative URLs (see LOCALIZED_DERIVATIVE_URL_RE)
  // bypass derivative building entirely: the src is already the final,
  // optimized files-host URL and must be passed through unchanged.
  const derivativeSrc = useMemo(() => {
    if (LOCALIZED_DERIVATIVE_URL_RE.test(src)) return null;
    const displayWidth = width ?? (fill ? undefined : 400);
    return buildImageDerivativeUrl(src, filesOrigin, {
      width: displayWidth === undefined ? undefined : displayWidth * 2,
      format: 'webp',
    });
  }, [src, filesOrigin, width, fill]);

  const resolvedSrc = useMemo(
    () => derivativeSrc ?? resolveAssetUrl(src, filesOrigin),
    [src, filesOrigin, derivativeSrc],
  );

  const resolvedFallbackSrc = useMemo(() => {
    const defaultFallback = `${filesOrigin}/images/placeholder.svg`;
    return resolveAssetUrl(fallbackSrc ?? defaultFallback, filesOrigin);
  }, [fallbackSrc, filesOrigin]);

  const safeResolvedSrc = useMemo(() => sanitizeUrl(resolvedSrc), [resolvedSrc]);
  const safeResolvedFallbackSrc = useMemo(
    () => sanitizeUrl(resolvedFallbackSrc) || resolvedFallbackSrc,
    [resolvedFallbackSrc],
  );

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [safeResolvedSrc]);

  useEffect(() => {
    // Images restored from the HTTP/memory cache can be complete before the
    // load event fires (or after it was missed between render and effect).
    // Clear the loading state immediately for those so the spinner/aria-busy
    // state is not stuck on cached images.
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setIsLoading(false);
    }
  }, []);

  const finalSrc = useMemo(() => {
    const primary = safeResolvedSrc || safeResolvedFallbackSrc;
    return hasError ? safeResolvedFallbackSrc : primary;
  }, [hasError, safeResolvedFallbackSrc, safeResolvedSrc]);

  const wrapperClasses = cx('overflow-hidden rounded-2xl bg-secondary', fill ? 'relative h-full w-full' : '', className);

  // Load-state classes must be DETERMINISTIC across the server render and the
  // first client render: an image is never loaded when the markup is first
  // rendered, so both emit `opacity-0`. Flipping to `opacity-100` happens via
  // a post-hydration state update (onLoad or the cached-image effect), which
  // is a normal re-render — not a hydration mismatch. Gating the initial class
  // on a `mounted` flag made the server emit no class while the client's
  // hydration-phase update produced `opacity-0`, causing React's attribute
  // mismatch warning on <img>.
  const imageClasses = cx(
    'transition-opacity duration-300',
    isLoading ? 'opacity-0' : 'opacity-100',
    imgClassName,
  );

  const style: CSSProperties = { objectFit, objectPosition, ...(imgStyle ?? {}) };

  // Derivatives are already optimized by the files host and must not be
  // processed again by Next's /_next/image optimizer. The same applies to
  // pre-built localized derivative URLs.
  const isPrebuiltDerivative = LOCALIZED_DERIVATIVE_URL_RE.test(src);
  const unoptimized =
    rest.unoptimized === true || derivativeSrc !== null || isPrebuiltDerivative;
  const { unoptimized: _ignoredUnoptimized, ...imageRest } = rest;

  const sharedImgProps = {
    ref: imgRef,
    style,
    unoptimized,
    onError: () => setHasError(true),
    onLoad: () => setIsLoading(false),
    ...imageRest,
  } as const;

  return (
    <div className={wrapperClasses} aria-busy={isLoading || undefined}>
      {fill ? (
        <NextImage
          src={finalSrc}
          alt={alt}
          fill
          className={imageClasses}
          {...sharedImgProps}
        />
      ) : (
        <NextImage
          src={finalSrc}
          alt={alt}
          width={width ?? 400}
          height={height ?? 300}
          className={imageClasses}
          {...sharedImgProps}
        />
      )}
    </div>
  );
}

Image.displayName = 'Image';